import { useMemo } from 'react';
import { uniqBy } from 'lodash';
import fuzzyHighlight from '@jetbrains/ring-ui/components/global/fuzzy-highlight';
import { useRestrictedPluginIds, useTbePlugins } from '@common/queries/plugins/plugins';
import { useBundledPlugins } from '@common/queries/plugins/bundled-plugins';
import { useMarketplacePlugins, useSearchMarketplacePlugins } from '@common/queries/plugins';
import { useFeatureFlag } from '@common/server-config-context/server-config-context';
export function useAllSourcePluginsSearch(filter, isRestrictedMode, fetchEnabled) {
    const allowDenyPluginsListEnabled = useFeatureFlag('allowDenyPluginsListEnabled');
    const { data: internalRepoPlugins, isFetching: isInternalRepoPluginsFetching, isLoading: isInternalRepoPluginsLoading } = useTbePlugins(fetchEnabled);
    const { data: bundledPlugins, isFetching: isBundledPluginsFetching, isLoading: isBundledPluginsLoading } = useBundledPlugins(fetchEnabled);
    const { data: marketPlacePluginById, isFetching: isMarketPlacePluginByIdFetching, isLoading: isMarketPlacePluginByIdLoading } = useMarketplacePlugins([filter], fetchEnabled);
    const { data: marketplacePlugins, isFetching: isMarketplacePluginsFetching, isLoading: isMarketplacePluginsLoading } = useSearchMarketplacePlugins(filter, fetchEnabled);
    const { data: restrictedModePlugins, isLoading: isRestrictedModePluginsLoading, isFetching: isRestrictedModePluginsFetching } = useRestrictedPluginIds(fetchEnabled);
    const internalRepoFiltered = internalRepoPlugins === null || internalRepoPlugins === void 0 ? void 0 : internalRepoPlugins.filter(plugin => fuzzyHighlight(filter, `${plugin.label}${plugin.key}`).matched);
    const bundledFiltered = bundledPlugins === null || bundledPlugins === void 0 ? void 0 : bundledPlugins.filter(plugin => fuzzyHighlight(filter, `${plugin.label}${plugin.key}`).matched);
    const plugins = useMemo(() => {
        const internalFilteredByRestriction = internalRepoFiltered && isRestrictedMode
            ? filterPluginsByIds(internalRepoFiltered, restrictedModePlugins !== null && restrictedModePlugins !== void 0 ? restrictedModePlugins : [])
            : internalRepoFiltered !== null && internalRepoFiltered !== void 0 ? internalRepoFiltered : [];
        const bundledFilteredByRestriction = bundledFiltered && isRestrictedMode
            ? filterPluginsByIds(bundledFiltered, restrictedModePlugins !== null && restrictedModePlugins !== void 0 ? restrictedModePlugins : [])
            : bundledFiltered !== null && bundledFiltered !== void 0 ? bundledFiltered : [];
        const marketplaceByIdFilteredByRestriction = marketPlacePluginById && isRestrictedMode
            ? filterPluginsByIds(marketPlacePluginById, restrictedModePlugins !== null && restrictedModePlugins !== void 0 ? restrictedModePlugins : [])
            : marketPlacePluginById !== null && marketPlacePluginById !== void 0 ? marketPlacePluginById : [];
        const marketplaceFilteredByRestriction = (marketplacePlugins && isRestrictedMode
            ? filterPluginsByIds(marketplacePlugins, restrictedModePlugins !== null && restrictedModePlugins !== void 0 ? restrictedModePlugins : [])
            : marketplacePlugins !== null && marketplacePlugins !== void 0 ? marketplacePlugins : []).filter(plugin => marketplaceByIdFilteredByRestriction.every(p => p.key !== plugin.key));
        return [].concat(internalFilteredByRestriction, bundledFilteredByRestriction, marketplaceByIdFilteredByRestriction, marketplaceFilteredByRestriction);
    }, [
        internalRepoFiltered,
        bundledFiltered,
        marketPlacePluginById,
        marketplacePlugins,
        restrictedModePlugins,
        isRestrictedMode
    ]);
    const isLoading = isBundledPluginsLoading ||
        isInternalRepoPluginsLoading ||
        isMarketPlacePluginByIdLoading ||
        isMarketplacePluginsLoading ||
        isRestrictedModePluginsLoading;
    const isFetching = isBundledPluginsFetching ||
        isInternalRepoPluginsFetching ||
        isMarketPlacePluginByIdFetching ||
        isMarketplacePluginsFetching ||
        isRestrictedModePluginsFetching;
    return {
        plugins: allowDenyPluginsListEnabled ? plugins : removeBundledDuplicates(plugins),
        isLoading,
        isFetching
    };
}
function filterPluginsByIds(plugins, excludedPluginIds) {
    return plugins.filter(plugin => excludedPluginIds.includes(plugin.key));
}
function removeBundledDuplicates(plugins) {
    return uniqBy(plugins.map(plugin => {
        if (plugin.source === 'BUNDLED') {
            return { ...plugin, source: 'MARKETPLACE' };
        }
        return plugin;
    }), 'key');
}
